import { useState, useCallback, useRef } from 'react';
// @ts-ignore - Prisma client types
import type {  User  } from '@prisma/client';

interface MentionState {
  isActive: boolean;
  searchText: string;
  position: { top: number; left: number };
  mentionStart: number;
}

export interface MentionData {
  id: string;
  name: string;
  username: string;
}

export function useMentions() {
  const [mentionState, setMentionState] = useState<MentionState>({
    isActive: false,
    searchText: '',
    position: { top: 0, left: 0 },
    mentionStart: 0,
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInput = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = event.target;
    const text = textarea.value;
    const caretPosition = textarea.selectionStart;
    
    // Find the last @ symbol before the caret
    const lastAtSymbol = text.lastIndexOf('@', caretPosition - 1);
    
    if (lastAtSymbol >= 0) {
      const textAfterAt = text.slice(lastAtSymbol + 1, caretPosition);
      const hasSpaceAfterAt = /\s/.test(textAfterAt);
      
      if (!hasSpaceAfterAt) {
        // Get position for the dropdown
        const { top, left } = getCaretCoordinates(textarea, lastAtSymbol);
        
        setMentionState({
          isActive: true,
          searchText: textAfterAt,
          position: { 
            top: top + 20, // Add some offset to show below the caret
            left 
          },
          mentionStart: lastAtSymbol,
        });
        return;
      }
    }
    
    // If we get here, there's no active mention
    setMentionState(prev => ({ ...prev, isActive: false, searchText: '' }));
  }, []);

  const insertMention = useCallback((user: User) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const text = textarea.value;
    const beforeMention = text.slice(0, mentionState.mentionStart);
    const afterMention = text.slice(textarea.selectionStart);
    
    // Insert the mention
    const mention = `@${user.username} `;
    const newText = beforeMention + mention + afterMention;
    
    // Update the textarea
    textarea.value = newText;
    
    // Move caret to after the mention
    const newCaretPosition = mentionState.mentionStart + mention.length;
    textarea.selectionStart = newCaretPosition;
    textarea.selectionEnd = newCaretPosition;
    
    // Reset mention state
    setMentionState(prev => ({ ...prev, isActive: false, searchText: '' }));
    
    // Focus back on textarea
    textarea.focus();
  }, [mentionState.mentionStart]);

  const closeMentions = useCallback(() => {
    setMentionState(prev => ({ ...prev, isActive: false, searchText: '' }));
  }, []);

  return {
    mentionState,
    textareaRef,
    handleInput,
    insertMention,
    closeMentions,
  };
}

// Helper function to get caret coordinates
function getCaretCoordinates(textarea: HTMLTextAreaElement, position: number) {
  const { offsetLeft, offsetTop } = textarea;
  const text = textarea?.value?.slice(0, position);
  
  // Create a hidden div to measure text
  const div = document.createElement('div');
  if (div?.style) { div.style.position = 'absolute'; }
  if (div?.style) { div.style.visibility = 'hidden'; }
  if (div?.style) { div.style.whiteSpace = 'pre-wrap'; }
  if (div?.style) { div.style.wordWrap = 'break-word'; }
  if (div?.style) { div.style.font = getComputedStyle(textarea).font; }
  if (div?.style) { div.style.padding = getComputedStyle(textarea).padding; }
  div.textContent = text;
  
  document?.body?.appendChild(div);
  const coordinates = {
    top: offsetTop + div.clientHeight,
    left: offsetLeft + div.clientWidth,
  };
  document?.body?.removeChild(div);
  
  return coordinates;
}